// Hook custom per recuperare recensioni Google tramite API.
// Nota: uso axios e TypeScript, con gestione sicura degli errori senza usare 'any'.

import { useState, useEffect } from 'react';
import axios from 'axios';
import appConfig from '../config/appConfig';

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipizzazione recensioni Google
export interface GoogleReview {
    author_name: string;
    rating: number; // da 1 a 5
    text: string;
    profile_photo_url: string;
    relative_time_description: string;
}

/**
 * useGetGoogleReviews
 * Recupera recensioni Google tramite API personalizzata.
 *
 * Nota per il prossimo dev:
 * - `reviews` contiene la lista delle recensioni
 * - `isLoading` true mentre la chiamata è in corso
 * - `error` contiene messaggio di errore in caso di fallimento
 */
export const useGetGoogleReviews = () => {
    const [reviews, setReviews] = useState<GoogleReview[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchReviews = async () => {
            if (!appConfig.features.googleReviews.enabled || !appConfig.features.googleReviews.placeId) {
                setIsLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${API_BASE_URL}/google-reviews`, {
                    params: {
                        placeId: appConfig.features.googleReviews.placeId,
                        sort: appConfig.features.googleReviews.displayType,
                        count: appConfig.features.googleReviews.numberOfReviews,
                    },
                });

                if (response.data.success) {
                    setReviews(response.data.reviews);
                } else {
                    throw new Error(response.data.error || 'Errore nel recupero delle recensioni.');
                }
            } catch (err: unknown) {
                let message = 'Errore imprevisto.';
                if (axios.isAxiosError(err)) {
                    message = err.response?.data?.error || err.message || message;
                } else if (err instanceof Error) {
                    message = err.message;
                }
                console.error("Errore recupero recensioni Google:", message);
                setError(message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReviews();
    }, []);

    return { reviews, isLoading, error };
};
